import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// columns: [{ key, label, format?(value,row) }]   rows: array of plain objects
export interface ExportColumn { key: string; label: string; format?: (value: any, row: any) => any; }
export interface ExportBranding { companyName?: string; logoUrl?: string; primaryColor?: string; secondaryColor?: string; }

const cell = (col: ExportColumn, row: any) => {
  const raw = row[col.key];
  if (col.format) return col.format(raw, row);
  return raw === undefined || raw === null ? '' : raw;
};
const stamp = () => new Date().toISOString().slice(0, 10);
const safe = (name?: string) => (name || 'export').replace(/[^a-z0-9_-]+/gi, '_');
const download = (filename: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};
const hexToRgb = (hex?: string): [number, number, number] => {
  if (!hex) return [26, 86, 219];
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};
const csvValue = (v: any) => {
  const s = v === undefined || v === null ? '' : String(v);
  return '"' + s.replace(/"/g, '""') + '"';
};
const loadImageDataURL = (url?: string): Promise<{ dataUrl: string; w: number; h: number } | null> => new Promise((resolve) => {
  if (!url) return resolve(null);
  const full = url.startsWith('http') ? url : window.location.origin + url;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth || 200; c.height = img.naturalHeight || 200;
      const ctx = c.getContext('2d');
      if (!ctx) return resolve(null);
      ctx.drawImage(img, 0, 0);
      resolve({ dataUrl: c.toDataURL('image/png'), w: c.width, h: c.height });
    } catch (e) { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = full;
});

export const exportToCSV = (filename: string, columns: ExportColumn[], rows: any[]) => {
  const header = columns.map(c => csvValue(c.label)).join(',');
  const body = rows.map(r => columns.map(c => csvValue(cell(c, r))).join(','));
  const csv = [header, ...body].join('\n');
  download(`${safe(filename)}_${stamp()}.csv`, new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
};

export const exportToExcel = (filename: string, columns: ExportColumn[], rows: any[]) => {
  const labels = columns.map(c => c.label);
  const data = rows.map(r => columns.map(c => cell(c, r)));
  const ws = XLSX.utils.aoa_to_sheet([labels, ...data]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${safe(filename)}_${stamp()}.xlsx`);
};

export const exportToPDF = async (filename: string, title: string, columns: ExportColumn[], rows: any[], branding: ExportBranding = {}) => {
  const doc: any = new jsPDF({ orientation: columns.length > 5 ? 'landscape' : 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const primary = hexToRgb(branding.primaryColor || '#1A56DB');
  const secondary = hexToRgb(branding.secondaryColor || '#059669');
  const companyName = (branding.companyName || 'Nexus HR').toUpperCase();

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageWidth, 30, 'F');
  let nameX = 12;
  const logo = await loadImageDataURL(branding.logoUrl);
  if (logo && logo.dataUrl) {
    const h = 18, w = Math.min(26, (logo.w / logo.h) * h);
    try { doc.addImage(logo.dataUrl, 'PNG', 12, 6, w, h); nameX = 12 + w + 6; } catch (e) {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text(companyName, nameX, 15);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(String(title || ''), nameX, 23);
  doc.setTextColor(0, 0, 0);

  autoTable(doc, {
    startY: 36,
    head: [columns.map(c => c.label)],
    body: rows.map(r => columns.map(c => { const v = cell(c, r); return v === undefined || v === null ? '' : String(v); })),
    styles: { fontSize: 8 },
    headStyles: { fillColor: primary },
    alternateRowStyles: { fillColor: [245, 247, 250] }
  });
  const y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : 40;
  doc.setDrawColor(secondary[0], secondary[1], secondary[2]); doc.setLineWidth(0.6); doc.line(12, y, pageWidth - 12, y);
  doc.setFontSize(8); doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleString()}`, 12, y + 6);
  doc.save(`${safe(filename)}_${stamp()}.pdf`);
};
